import {
  type InfiniteData,
  type UseQueryResult,
  useInfiniteQuery,
  useQuery
} from '@tanstack/react-query'
import React from 'react'
import type {
  ApplicantAvailabilitiesResponse,
  ApplicantInfosLDResponse,
  ApplicantInfosResponse,
  ApplicantPicturesResponse,
  AvailabilityData
} from '~/api/queries/applicant'
import type { UserPicturesResponse } from '~/api/queries/user'
import { fetchApiWithToken } from '~/api/utils'

export const orderStatus = [
  { id: 0, name: 'Fermée' },
  { id: 1, name: 'Ouverte' },
  { id: 2, name: 'Abandonnée' }
]

export const orderFinalStatusDetail = [
  { id: 1, name: 'Servie par nous' },
  { id: 2, name: 'Servie par concurrence' },
  { id: 3, name: 'Annulée par nous' },
  { id: 4, name: 'Annulée par client' }
]

export type OrderApplicant = {
  id: number
  firstname: string
  lastname: string
  availability?: AvailabilityData
  picture?: string
}

export type OrderInfosResponse = {
  id: number
  qualification?: {
    // @todo should not be possible to be null but API can return null ATM
    name: string
    id: number
  }
  jobTitle: string
  company: {
    name: string
    id: number
  }
  contact: string
  urgency?: number
  status: number
  statusDetail: number
  created: string
  applicants: {
    applicant: OrderApplicant
    status: {
      id: number
      name: string
    }
    created: string
    modified: string
  }[]
  users: {
    id: number
    firstname: string
    lastname: string
    picture?: string
  }[]
  segmentations: {
    id: number
    name: string
  }[]
  salaryMin: number
  salaryMax: number
  studyLevel: {
    id: number
    name: string
  } | null
  studyYearsLow: number | null
  studyYearsHigh: number | null
  location: string
  locationCenter: {
    address: string
    geometry: {
      location: {
        lat: number
        lng: number
      }
    }
  }
  locationRadius: number
  contractTerm: {
    id: number
    name: string
  } | null
  contractStart: string
  contractEnd: string
  authorizations: {
    id: number
    name: string
  }[]
  languages: {
    language: {
      id: number
      name: string
    }
    languageLevel: number[] | null
  }[]
  softwares: {
    software: {
      id: number
      name: string
    }
    softwareLevel: number[] | null
  }[]
  experiences: {
    id: number
    name: string
  }[]
  applicantAcceptedCount: number
  applicantWantedCount: number
  matchingLocation?: {
    address: string
    lat: number
    lng: number
  }
  applicantCountByStatus: {
    id: number
    applicantCount: number
  }[]
}

export type OrderCollectionReponse = Omit<OrderInfosResponse, 'applicants'>

export type OrderInfosLDResponse = {
  'hydra:totalItems': number
  'hydra:member': (OrderInfosResponse | OrderCollectionReponse)[]
}

const extractUserIdsFromOrders = (orderlist: OrderCollectionReponse[]) => {
  return orderlist.reduce((acc, order) => {
    for (const user of order.users) {
      acc.push(user.id)
    }
    return acc
  }, [] as number[])
}

type OrderListFilters = {
  sort?: string
  sortDirection?: 'asc' | 'desc'
}

export const useOrderListWithFilters = ({
  enabled = true,
  initialFilters = {}
}: { enabled?: boolean; initialFilters?: OrderListFilters }): {
  orderlist: OrderCollectionReponse[] | undefined
  orderlistTotal: number | undefined
  isOrderListLoading: boolean
  isOrderListError: boolean
  setFilters: React.Dispatch<React.SetStateAction<OrderListFilters>>
  fetchNextOrderListPage: () => void
  hasNextOrderListPage: boolean
  isFetchingNextOrderListPage: boolean
} => {
  const [filters, setFilters] = React.useState<OrderListFilters>(initialFilters)
  const { sort, sortDirection, ...restFilters } = filters
  let urlParams = { ...restFilters }
  if (sort && sortDirection) {
    const key = `order[${sort}]`
    urlParams = {
      ...urlParams,
      [key]: sortDirection
    }
  }
  const {
    data,
    isLoading: isOrderListLoading,
    fetchNextPage: fetchNextOrderListPage,
    hasNextPage: hasNextOrderListPage,
    isError: isOrderListError,
    isFetchingNextPage: isFetchingNextOrderListPage
  } = useInfiniteQuery<
    OrderInfosLDResponse,
    Error,
    InfiniteData<OrderInfosLDResponse>
  >({
    queryKey: ['orders', 'search', urlParams],
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length
      if (lastPage['hydra:totalItems'] > currentPage * 30) {
        return currentPage + 1
      }
    },
    queryFn: async ({ pageParam, signal }) => {
      const orders = (await fetchApiWithToken({
        method: 'get',
        url: `/v1/orders?page=${pageParam}&${new URLSearchParams(urlParams)}`,
        useLDJson: true,
        signal
      })) as OrderInfosLDResponse
      const userIds = extractUserIdsFromOrders(orders['hydra:member'])
      const userPictures: UserPicturesResponse[] = await fetchApiWithToken({
        method: 'get',
        url: `/v1/users/profile_pics?id[]=${userIds.join('&id[]=')}`
      })
      orders['hydra:member'] = orders['hydra:member'].map((order) => {
        return {
          ...order,
          users: order.users.map((user) => {
            const picture = userPictures.find(
              (picture) => picture.user === `/v1/users/${user.id}`
            )
            return {
              ...user,
              picture: picture?.profilePicUrl
            }
          })
        }
      })
      return orders as OrderInfosLDResponse
    },
    refetchOnWindowFocus: false,
    staleTime: 1800_000,
    enabled
  })
  return {
    orderlist: data?.pages.flatMap((page) => page['hydra:member']),
    orderlistTotal: data?.pages[0]?.['hydra:totalItems'],
    isOrderListLoading,
    isOrderListError,
    setFilters,
    fetchNextOrderListPage,
    hasNextOrderListPage,
    isFetchingNextOrderListPage
  }
}

export const useOrdersSearch = ({
  userId,
  search,
  ids,
  onlyOpen = false,
  enabled = true
}: {
  userId?: number | null
  ids?: number[]
  search?: string
  enabled?: boolean
  onlyOpen?: boolean
}) => {
  return useInfiniteQuery<
    OrderInfosLDResponse,
    Error,
    InfiniteData<OrderInfosLDResponse>
  >({
    queryKey: ['orders', 'search', { userId, search }],
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length
      if (lastPage['hydra:totalItems'] > currentPage * 30) {
        return currentPage + 1
      }
      return undefined
    },
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams()
      params.append('page', (pageParam as number).toString())
      if (userId) {
        params.append('user', userId.toString())
      }
      if (search) {
        params.append('q', search)
      }
      if (ids) {
        params.append('id[]', ids.join('&id[]='))
      }
      if (onlyOpen) {
        params.append('status', '1')
      }
      params.append('order[created]', 'desc')

      const orders = (await fetchApiWithToken({
        method: 'get',
        url: `/v1/orders?${params.toString()}`,
        useLDJson: true,
        signal,
      })) as OrderInfosLDResponse

      const userIds = extractUserIdsFromOrders(orders['hydra:member'])
      if (userIds.length > 0) {
        const userPictures: UserPicturesResponse[] = await fetchApiWithToken({
          method: 'get',
          url: `/v1/users/profile_pics?id[]=${userIds.join('&id[]=')}`,
          signal,
        })
        orders['hydra:member'] = orders['hydra:member'].map((order) => ({
          ...order,
          users: order.users.map((user) => {
            const picture = userPictures.find(
              (pic) => pic.user === `/v1/users/${user.id}`
            )
            return {
              ...user,
              picture: picture?.profilePicUrl,
            }
          }),
        }))
      }
      return orders
    },
    enabled,
    staleTime: 60_000,
  })
}

export type MatchingFilter = {
  softwares?: { id: number; level?: number[] }[]
  languages?: { id: number; level?: number[] }[]
  experiences?: number[]
  contractTerm?: number
  salaryMin?: number
  salaryMax?: number
  location?: string
  locationRadius?: number
  locationCenter?: { lat: number; lng: number }
  authorizations?: number[]
  workedForUs?: boolean
  segmentations?: number[]
  studyYearsLow?: number
  studyYearsHigh?: number
}

export const useOrder = (
  id?: number
): UseQueryResult<OrderInfosResponse, Error> =>
  useQuery({
    queryKey: ['order', id],
    queryFn: async ({ signal }) =>
      await fetchApiWithToken({
        method: 'get',
        url: `/v1/orders/${id}`,
        signal
      }),
    enabled: !!id
  })

const convertMatchingFilterToQueryParams = (filters: MatchingFilter) => {
  let queryParams = {}
  if (filters.softwares) {
    queryParams = {
      ...queryParams,
      softwares: filters.softwares
        .map(
          (software) =>
            `${software.id}${software.level ? `:${software.level.join(',')}` : ''}`
        )
        .join(',')
    }
  }
  if (filters.languages) {
    queryParams = {
      ...queryParams,
      languages: filters.languages
        .map(
          (language) =>
            `${language.id}${language.level ? `:${language.level.join(',')}` : ''}`
        )
        .join(',')
    }
  }
  if (filters.experiences) {
    queryParams = {
      ...queryParams,
      experiences: filters.experiences.join(',')
    }
  }
  if (filters.contractTerm) {
    queryParams = {
      ...queryParams,
      jobTypes: filters.contractTerm
    }
  }
  if (filters.salaryMin) {
    queryParams = {
      ...queryParams,
      'jobTypes.remunerationAccepted[gte]': filters.salaryMin
    }
  }
  if (filters.salaryMax) {
    queryParams = {
      ...queryParams,
      'jobTypes.remunerationAccepted[lte]': filters.salaryMax
    }
  }
  if (filters.studyYearsLow) {
    queryParams = {
      ...queryParams,
      'study[gte]': filters.studyYearsLow
    }
  }
  if (filters.studyYearsHigh) {
    queryParams = {
      ...queryParams,
      'study[lte]': filters.studyYearsHigh
    }
  }
  if (filters.locationRadius) {
    queryParams = {
      ...queryParams,
      'address[radius]': filters.locationRadius
    }
  }
  if (filters.locationCenter) {
    queryParams = {
      ...queryParams,
      'address[lat]': filters.locationCenter.lat,
      'address[lng]': filters.locationCenter.lng
    }
  }
  if (filters.authorizations) {
    queryParams = {
      ...queryParams,
      authorizations: filters.authorizations.join(',')
    }
  }
  if (filters.workedForUs) {
    queryParams = {
      ...queryParams,
      hasWorkedForUs: filters.workedForUs ? 'true' : 'false'
    }
  }
  if (filters.segmentations) {
    queryParams = {
      ...queryParams,
      segmentations: filters.segmentations.join(',')
    }
  }
  return queryParams
}

export const useOrderApplicantMatching = (
  order?: OrderInfosResponse,
  filters: MatchingFilter = {},
  enabled = true
): {
  applicantMatching: ApplicantInfosResponse[] | undefined
  applicantMatchingTotal: number | undefined
  isApplicantMatchingLoading: boolean
  fetchNextApplicantPage: () => void
  hasNextApplicantPage: boolean
  isFetchingNextApplicantPage: boolean
} => {
  const {
    data,
    isLoading: isApplicantMatchingLoading,
    fetchNextPage: fetchNextApplicantPage,
    hasNextPage: hasNextApplicantPage,
    isFetchingNextPage: isFetchingNextApplicantPage
  } = useInfiniteQuery<
    ApplicantInfosLDResponse,
    Error,
    InfiniteData<ApplicantInfosLDResponse>
  >({
    queryKey: ['order', order?.id, 'matching', filters],
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length
      if (lastPage['hydra:totalItems'] > currentPage * 30) {
        return currentPage + 1
      }
    },
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        excludeApplicantsOfOrder: String(order?.id),
        ...convertMatchingFilterToQueryParams(filters)
      })
      if (order?.qualification) {
        params.append('qualifications', String(order.qualification.id))
      }
      const applicants = (await fetchApiWithToken({
        method: 'get',
        url: `/v1/applicants?page=${pageParam}&${params}`,
        useLDJson: true
      })) as ApplicantInfosLDResponse
      if (applicants['hydra:member'].length > 0) {
        await Promise.all([
          fetchApiWithToken<ApplicantAvailabilitiesResponse[]>({
            method: 'get',
            url: `/v1/applicants/availabilities?id[]=${applicants['hydra:member'].map((applicant) => applicant.id).join('&id[]=')}`
          }).then(
            (applicantAvailabilities: ApplicantAvailabilitiesResponse[]) => {
              applicants['hydra:member'] = applicants['hydra:member'].map(
                (applicant) => {
                  const availability = applicantAvailabilities.find(
                    (availability) =>
                      availability.applicant ===
                      `/v1/applicants/${applicant.id}`
                  )
                  return {
                    ...applicant,
                    availability: availability?.availability
                  }
                }
              )
            }
          ),
          fetchApiWithToken<ApplicantPicturesResponse[]>({
            method: 'get',
            url: `/v1/applicants/profile_pics?id[]=${applicants['hydra:member'].map((applicant) => applicant.id).join('&id[]=')}`
          }).then((applicantPictures: ApplicantPicturesResponse[]) => {
            applicants['hydra:member'] = applicants['hydra:member'].map(
              (applicant) => {
                const picture = applicantPictures.find(
                  (picture) =>
                    picture.applicant === `/v1/applicants/${applicant.id}`
                )
                return {
                  ...applicant,
                  picture: picture?.profilePicUrl
                }
              }
            )
          })
        ])
      }
      return applicants as ApplicantInfosLDResponse
    },
    refetchOnWindowFocus: false,
    enabled: !!order && enabled,
    staleTime: 1800_000
  })
  return {
    applicantMatching: data?.pages.flatMap((page) => page['hydra:member']),
    applicantMatchingTotal: data?.pages[0]?.['hydra:totalItems'],
    isApplicantMatchingLoading,
    fetchNextApplicantPage,
    hasNextApplicantPage,
    isFetchingNextApplicantPage
  }
}

type ApplicantIdsLDResponse = {
  'hydra:totalItems': number
  'hydra:member': number[]
}

export const useOrderApplicantMatchingIds = (
  order?: OrderInfosResponse,
  filters: MatchingFilter = {},
  enabled = true
): {
  applicantsIds: number[] | undefined
  isApplicantsIdsLoading: boolean
} => {
  const { data: applicantsIds, isLoading: isApplicantsIdsLoading } = useQuery<
    ApplicantIdsLDResponse,
    Error
  >({
    queryKey: ['order', order?.id, 'matching_ids', filters],
    queryFn: () => {
      const params = new URLSearchParams({
        excludeApplicantsOfOrder: String(order?.id),
        ...convertMatchingFilterToQueryParams(filters)
      })
      if (order?.qualification) {
        params.append('qualifications', String(order.qualification.id))
      }
      return fetchApiWithToken({
        method: 'get',
        url: `/v1/applicants/ids?${params}`,
        useLDJson: true
      })
    },
    refetchOnWindowFocus: false,
    enabled: !!order && enabled,
    staleTime: 1800_000
  })
  return {
    applicantsIds: applicantsIds?.['hydra:member'],
    isApplicantsIdsLoading
  }
}

type OrderHistoryEvent = {
  id: number
  created: string
  createdBy: string
  type: 'status_update' | 'meeting_applicant' | 'recall_applicant' | 'send_email'
  orderStatus: string
  notes: string | null
  cancelled: boolean
  done: boolean | null
  date: string | null
  assignedUser: string | null
  order: string
  applicants: (
    | string
    | {
      id: number
      firstname: string
      lastname: string
      picture: string
    }
  )[]
  contacts: (
    | string
    | {
      id: number
      firstname: string
      lastname: string
      picture: string
    }
  )[]
}

type OrderHistoryEventLDResponse = {
  'hydra:member': OrderHistoryEvent[]
  'hydra:totalItems': number
}

export const useOrderHistory = ({
  orderId
}: {
  orderId: number
}): {
  items: OrderHistoryEvent[] | undefined
  itemsTotal: number | undefined
  isLoading: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
} => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<
      OrderHistoryEventLDResponse,
      Error,
      InfiniteData<OrderHistoryEventLDResponse>
    >({
      queryKey: ['order', orderId, 'history'],
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const currentPage = allPages.length
        if (lastPage['hydra:totalItems'] > currentPage * 30) {
          return currentPage + 1
        }
      },
      queryFn: async ({ pageParam, signal }) => {
        const events: OrderHistoryEventLDResponse = await fetchApiWithToken({
          method: 'get',
          signal,
          url: `/v1/history?order=${orderId}&page=${pageParam}`,
          useLDJson: true
        })
        const applicantIds = events['hydra:member'].reduce((acc, event) => {
          for (const applicant of event.applicants) {
            if (typeof applicant === 'string') {
              const applicantId = Number.parseInt(
                applicant.replace('/v1/applicants/', '')
              )
              if (!acc.includes(applicantId)) {
                acc.push(applicantId)
              }
            }
          }
          return acc
        }, [] as number[])
        const [applicantPictures, applicantData] = await Promise.all([
          fetchApiWithToken<ApplicantPicturesResponse[]>({
            method: 'get',
            signal,
            url: `/v1/applicants/profile_pics?id[]=${applicantIds.join('&id[]=')}&itemsPerPage=1000`
          }),
          fetchApiWithToken<
            {
              id: number
              firstname: string
              lastname: string
            }[]
          >({
            method: 'get',
            signal,
            useLDJson: false,
            url: `/v1/applicants?id[]=${applicantIds.join('&id[]=')}&itemsPerPage=1000&fields[]=id&fields[]=firstname&fields[]=lastname`
          })
        ])
        events['hydra:member'] = events['hydra:member'].map((event) => {
          return {
            ...event,
            applicants: event.applicants.map((applicant) => {
              const picture = applicantPictures.find(
                (picture) => picture.applicant === applicant
              )
              const data = applicantData.find(
                (app) => `/v1/applicants/${app.id}` === applicant
              )
              if (picture && data) {
                return {
                  ...data,
                  picture: picture.profilePicUrl
                }
              }
              return applicant
            })
          }
        })
        return events
      },
      enabled: !!orderId,
      staleTime: 300_000
    })
  return {
    items: data?.pages.flatMap((page) => page['hydra:member']),
    itemsTotal: data?.pages[0]?.['hydra:totalItems'],
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  }
}
